import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import TrendSparkline from "../components/TrendSparkline";
import { freshnessLabel, freshnessBadgeClasses, fmtMetric } from "../lib/metricEnvelope";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Read the pinned customer id from sessionStorage — same pattern as
 * FinancialSyncPage.jsx + the rest of the app. Returns "" when no
 * session has been set, which surfaces an unauthenticated state.
 */
function readCustomerId() {
  try {
    const raw = sessionStorage.getItem("current_customer");
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    return parsed?.id || "";
  } catch {
    return "";
  }
}

/**
 * Trends page — historical timeseries of every metric in the
 * Business Intelligence catalog. Reads from
 * /api/business-intelligence/timeseries which pulls validated
 * financial_snapshots (never raw uploads) and runs them through the
 * calculation engine per period.
 *
 * Layout: 3-column grid of cards. Each card shows
 *   • metric label
 *   • latest value with freshness label ("As on 30-Jun-2026 · Snapshot")
 *   • compact period-over-period delta arrow
 *   • sparkline of the full available history
 */
export default function TrendsPage() {
  const customerId = readCustomerId();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!customerId) {
      setLoading(false);
      setError("Please sign in to view trends.");
      return;
    }
    (async () => {
      try {
        const r = await fetch(
          `${API}/api/business-intelligence/timeseries`,
          { credentials: "include" },
        );
        const body = await r.json();
        if (cancelled) return;
        if (!r.ok) {
          setError(body?.detail || `HTTP ${r.status}`);
        } else {
          setData(body);
        }
      } catch (e) {
        if (!cancelled) setError(String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customerId]);

  if (loading) {
    return (
      <div className="px-6 py-10 text-[13px] text-slate-500"
           data-testid="trends-loading">Loading trends…</div>
    );
  }
  if (error) {
    return (
      <div className="px-6 py-10 text-[13px] text-rose-600"
           data-testid="trends-error">{error}</div>
    );
  }
  const periods = data?.periods || [];
  const series  = data?.series  || {};
  const catalog = data?.catalog || {};
  const orderedMetricIds = data?.available_metrics || Object.keys(catalog);

  if (periods.length === 0) {
    return (
      <div className="px-6 py-10" data-testid="trends-empty">
        <h1 className="text-3xl font-semibold text-slate-900 mb-2">Trends</h1>
        <p className="text-[14px] text-slate-600 mb-6">
          Trends appear here automatically once you've uploaded statements for
          more than one period via Financial Sync (or have multiple Tally syncs).
        </p>
        <Link to="/financial-sync"
              data-testid="trends-go-financial-sync"
              className="inline-flex items-center rounded-lg bg-emerald-600 px-4 py-2 text-[13px] font-medium text-white hover:bg-emerald-700">
          Upload a statement →
        </Link>
      </div>
    );
  }

  return (
    <div className="px-4 tablet:px-5 desktop:px-6 py-6 desktop:py-8 max-w-6xl mx-auto" data-testid="trends-page">
      <h1 className="text-3xl font-semibold text-slate-900 mb-1">
        Financial Trends
      </h1>
      <p className="text-[14px] text-slate-600 mb-6 max-w-2xl">
        Period-over-period analytics across every validated upload.
        Every value carries its source — never invented, always traceable.
      </p>

      <div className="mb-6 flex items-center gap-2 text-[12px] text-slate-500"
           data-testid="trends-period-strip">
        <span className="font-medium text-slate-700">
          {periods.length} period{periods.length === 1 ? "" : "s"}:
        </span>
        {periods.map((p, idx) => (
          <span key={idx}
                className={`inline-flex items-center rounded-full border px-2 py-0.5 ${freshnessBadgeClasses(p)}`}
                data-testid={`trends-period-${idx}`}>
            {p.period_fy || p.period_end} · {p.frequency || "Snapshot"}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-3 gap-4">
        {orderedMetricIds.map((mid) => {
          const meta  = catalog[mid] || {};
          const arr   = series[mid] || [];
          if (arr.length === 0) return null;
          const latest = arr[arr.length - 1];
          const prev   = arr.length > 1 ? arr[arr.length - 2] : null;
          const values = arr.map((e) => (typeof e.value === "number" ? e.value : null));

          let deltaPct = null;
          if (prev?.value && latest?.value) {
            deltaPct = ((latest.value - prev.value) / Math.abs(prev.value)) * 100;
          }

          return (
            <div key={mid}
                 data-testid={`trend-card-${mid}`}
                 className="rounded-xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <div className="flex items-baseline justify-between">
                <div className="text-[12.5px] font-medium text-slate-500">{meta.label}</div>
                {deltaPct !== null && (
                  <span data-testid={`trend-delta-${mid}`}
                        className={`text-[11.5px] font-semibold ${deltaPct >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                    {deltaPct >= 0 ? "+" : ""}{deltaPct.toFixed(1)}%
                  </span>
                )}
              </div>
              <div className="mt-1 text-[22px] font-bold text-[#0F1B2D]"
                   data-testid={`trend-latest-${mid}`}>
                {fmtMetric(latest?.value, meta.unit)}
              </div>
              <div className={`mt-1 inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] ${freshnessBadgeClasses(latest)}`}
                   data-testid={`trend-freshness-${mid}`}>
                {freshnessLabel(latest)}
              </div>
              <div className="mt-3">
                <TrendSparkline values={values} color="auto"
                                testId={`trend-spark-${mid}`} filled />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
